/**
 * Persian-Indic digits for every number a human reads.
 *
 * Not cosmetic. A parent reading a Persian sentence with Latin digits is reading
 * something that looks machine-generated, and this product's whole position is that its
 * restraint is trustworthy — which is a judgement a parent makes partly on how the page
 * looks. Grouping separators are off because these are small counts, not amounts.
 */
const DIGITS = new Intl.NumberFormat("fa-IR", { useGrouping: false });

export const fa = (n: number): string => DIGITS.format(n);

/**
 * Reads a number a parent typed, accepting either digit set.
 *
 * A Persian keyboard produces ۰-۹ and a numeric touch keypad produces 0-9, often on the
 * same device. Rejecting one of them would look like a wrong answer.
 */
/**
 * Persian and Arabic-Indic digits rewritten as Latin ones, leaving everything else alone.
 *
 * **Every field that takes a number needs this, and forgetting it is silent.** A Persian keyboard
 * produces ۱۲۳۴۵۶; `\d` in JavaScript matches only `0-9`, so a field that filtered with `/\D/g` was
 * deleting the digits as they were typed and staying empty — which reads as a broken input rather than
 * as a locale problem, on the one screen nobody can get past.
 *
 * Both ranges, because Persian (U+06F0) and Arabic (U+0660) digits look near-identical and which one a
 * keyboard emits is not something the app gets to choose.
 */
export function toLatinDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

/** Just the digits of a string, in Latin form. Anything else — spaces, dashes, letters — is dropped. */
export const digitsOnly = (input: string): string =>
  toLatinDigits(input).replace(/[^0-9]/g, "");

export function parseDigits(input: string): number | null {
  const latin = toLatinDigits(input.trim());
  if (!/^\d+$/.test(latin)) return null;
  return Number(latin);
}

/**
 * A date as a parent reads dates: Jalali, in Persian.
 *
 * Through `Intl` with the `fa-IR` locale rather than a conversion library. The calendar arithmetic
 * is in the platform already, it is correct, and adding a dependency to restate it would be a
 * bundle cost for a worse answer — this is one of very few places the product shows a date at all.
 *
 * The Gregorian fallback is for a runtime without the locale data. Showing the wrong calendar is
 * poor; showing "Invalid Date" beside a subscription's end date is worse.
 */
const JALALI = (() => {
  try {
    return new Intl.DateTimeFormat("fa-IR", { dateStyle: "long" });
  } catch {
    return null;
  }
})();

export function faDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return JALALI ? JALALI.format(date) : date.toISOString().slice(0, 10);
}
